import React, { useEffect, useState } from "react";
import { useNavigation } from "../../../hooks";
import { Dataset, getDatasets } from "../../../services/consentService";
import LoadingSpinner from "../../common/LoadingSpinner";
import Icon from "../../ui/Icon";

const ConsentHistoryPage: React.FC = () => {
  const { goBack } = useNavigation();
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDatasets = async () => {
      try {
        setLoading(true);
        const datasetsData = await getDatasets();
        setDatasets(datasetsData);
      } catch (err) {
        console.error("항목 동의 내역 조회 실패:", err);
        setError("항목 동의 내역을 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchDatasets();
  }, []);

  // 데이터셋을 카테고리별로 분류
  // provideInstitutionNo가 null이 아니면 공공기관, null이면 의료기관 (공통 데이터셋)
  const publicDatasets = datasets.filter(
    (dataset) => dataset.provideInstitutionNo !== null
  );

  const medicalDatasets = datasets.filter(
    (dataset) => dataset.provideInstitutionNo === null
  );

  const getStatusBadge = (status?: "AGREEMENT" | "NONE") => {
    if (status === "AGREEMENT") {
      return (
        <span className="px-3 py-1 text-sm font-medium text-blue-700 bg-blue-50 rounded-full">
          동의
        </span>
      );
    }
    return (
      <span className="px-3 py-1 text-sm font-medium text-gray-500 bg-gray-100 rounded-full">
        미동의
      </span>
    );
  };

  if (loading) {
    return (
      <div className="h-screen-dvh bg-white flex flex-col">
        <header className="bg-white px-4 py-3 flex items-center">
          <button
            onClick={goBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Icon name="chevron-left" className="text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-800">항목동의 내역</h1>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen-dvh bg-white flex flex-col">
        <header className="bg-white px-4 py-3 flex items-center">
          <button
            onClick={goBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Icon name="chevron-left" className="text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-800">항목동의 내역</h1>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen-dvh bg-white flex flex-col">
      {/* 상단 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center">
        <button
          onClick={goBack}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon name="chevron-left" className="text-gray-600" />
        </button>      </header>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          {/* 공공기관 섹션 */}
          {publicDatasets.length > 0 && (
            <section>
              <h2 className="text-base font-semibold text-gray-900 mb-3">
                공공기관
              </h2>
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {publicDatasets.map((dataset, index) => (
                  <div
                    key={dataset.datasetCode}
                    className={`flex items-center justify-between px-4 py-3 ${
                      index !== publicDatasets.length - 1
                        ? "border-b border-gray-100"
                        : ""
                    }`}
                  >
                    <span className="text-gray-800 text-sm">
                      {dataset.datasetName}
                    </span>
                    {getStatusBadge(dataset.agreementStatus)}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 의료기관 섹션 */}
          {medicalDatasets.length > 0 && (
            <section>
              <h2 className="text-base font-semibold text-gray-900 mb-3">
                의료기관
              </h2>
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {medicalDatasets.map((dataset, index) => (
                  <div
                    key={dataset.datasetCode}
                    className={`flex items-center justify-between px-4 py-3 ${
                      index !== medicalDatasets.length - 1
                        ? "border-b border-gray-100"
                        : ""
                    }`}
                  >
                    <span className="text-gray-800 text-sm">
                      {dataset.datasetName}
                    </span>
                    {getStatusBadge(dataset.agreementStatus)}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 데이터가 없을 경우 */}
          {datasets.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">등록된 항목이 없습니다.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ConsentHistoryPage;
